import { numTrialDays } from 'Billing/Constants';
import { ActiveSubscriptionValues } from 'Utils/Subscription';

/** Why the paywall is on screen, as far as the account record can tell. */
export type PaywallReason =
  | 'trial-running'
  | 'subscribed'
  | 'trial-ended'
  | 'subscription-ended'
  | 'ended';

/** The fields of the account record the heading is decided from. */
export interface PaywallAccount {
  subscription_status?: string;
  subscription_provider?: string | null;
  createdAt?: string | null;
  /** When the backend last changed `subscription_status`: for a lapsed account, when it lapsed. */
  subscription_status_changed_at?: string | null;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;
/**
 * An account that lapsed this soon after it was created cannot have finished a
 * paid period: its free trial alone lasts `numTrialDays`. One day of margin
 * covers the hours around the end of the trial.
 */
const OLDEST_TRIAL_ONLY_ACCOUNT_IN_MS = (numTrialDays + 1) * DAY_IN_MS;

/**
 * How old the account was when it lapsed, or NaN when the record cannot say.
 * The backend stamps `subscription_status_changed_at` each time the status
 * changes, so for a lapsed account it is the moment of the lapse and the answer
 * stays the same on every later day. A record without that time (older
 * accounts, or a status refreshed without it) is measured against `now`, which
 * can only make the account look older, never younger.
 */
function accountAgeAtLapseInMs(account: PaywallAccount | null | undefined, now: Date): number {
  const createdAtInMs = Date.parse(account?.createdAt ?? '');
  const lapsedAtInMs = Date.parse(account?.subscription_status_changed_at ?? '');
  return (Number.isFinite(lapsedAtInMs) ? lapsedAtInMs : now.getTime()) - createdAtInMs;
}

/**
 * Decides the paywall's wording from the account record alone.
 *
 * - Still entitled (the paywall was opened by choice): no "ended" wording.
 * - Billed by the App Store or Google Play: the stores sell no free trial
 *   here, so a lapsed store account had a subscription.
 * - Otherwise the record does not say whether a web account ever paid. Only
 *   an account that lapsed too young to have paid is told its trial ended;
 *   every other lapsed account gets wording that is true either way.
 */
export function paywallReasonFor(
  account: PaywallAccount | null | undefined,
  now: Date,
): PaywallReason {
  const status = account?.subscription_status ?? '';
  if (status === 'trialing') return 'trial-running';
  if (ActiveSubscriptionValues.includes(status)) return 'subscribed';
  if (account?.subscription_provider === 'apple' || account?.subscription_provider === 'google')
    return 'subscription-ended';
  const ageAtLapseInMs = accountAgeAtLapseInMs(account, now);
  const lapsedTooYoungToHavePaid =
    Number.isFinite(ageAtLapseInMs) &&
    ageAtLapseInMs >= 0 &&
    ageAtLapseInMs <= OLDEST_TRIAL_ONLY_ACCOUNT_IN_MS;
  return lapsedTooYoungToHavePaid ? 'trial-ended' : 'ended';
}

interface PaywallCopy {
  heading: string;
  /** The sentence before "We'd also love to hear any feedback you have." */
  lead: string;
}

const SELECT_A_PLAN = 'Please select a payment plan to continue playing.';

export const PAYWALL_COPY: Record<PaywallReason, PaywallCopy> = {
  'trial-running': { heading: 'Choose your plan', lead: 'Your free trial is still running.' },
  subscribed: { heading: 'Choose your plan', lead: '' },
  'trial-ended': {
    heading: 'Your free trial has ended.',
    lead: `We hope you enjoyed your ${numTrialDays}-day free trial! ${SELECT_A_PLAN}`,
  },
  'subscription-ended': {
    heading: 'Your subscription has ended.',
    lead: `Welcome back! ${SELECT_A_PLAN}`,
  },
  ended: { heading: 'Your free trial or subscription has ended.', lead: SELECT_A_PLAN },
};
